import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import {
  getSystem,
  patchSystem,
  patchUrlDetailStatus,
  getMailDelivery,
  patchMailDelivery,
  getMenuDisplay,
  patchMenuDisplay,
  getCustomAlertList,
  postCustomAlert,
  patchCustomAlert,
  deleteCustomAlert,
  patchCustomAlertStatus,
  deleteAdministrator,
  getAdministratorList,
  patchAdministrator,
  postAdministrator,
  getCompanyService,
  getMfaQrCode,
  patchMfaAuth,
  deleteMfaAuth,
  getSlackChannelList,
  getIpRestrictionList,
  patchIpRestrictionList,
  getOfficeIpList,
  patchOfficeIpList,
  getAllowAccess,
} from '../../apis/setting'
import { setErrors } from '../common/errorsSlice'
import { updateCommonMfaAuth, deleteCommonMfaAuth } from '../common/commonPartSlice'
import {
  System,
  RecordData,
  UrlDetailStatusRequest,
  MailDelivery,
  MenuDisplay,
  getCustomAlertListResponse,
  postCustomAlertRequest,
  patchCustomAlertRequest,
  deleteCustomAlertRequest,
  patchCustomAlertForStatusRequest,
  deleteAdministratorRequest,
  patchAdministratorRequest,
  postAdministratorRequest,
  getAdministratorListResponse,
  CompanyService,
  patchMfaAuthRequest,
  SlackChannelList,
  IpRestrictionList,
  patchIpRestrictionListRequest,
  OfficeIpList,
  patchOfficeIpListRequest,
} from '../../types/setting'

export interface SystemState extends System {
  isLoading: boolean
}

const initialState: Partial<SystemState> = {
  google_account: null,
  microsoft_account: null,
  cybozu_account: null,
  is_available_cybozu: false,
  mail_delivery: null,
  menu_display: null,
  custom_alert_list: null,
  administrator_list: null,
  company_service: null,
  mfa_qr_code_uri: "",
  slack_channel_list: null,
  ip_restriction_list: null,
  office_ip_list: null,
  isLoading: true,
}

const systemSlice = createSlice({
  name: 'system',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    getSystemSuccess: (state, action: PayloadAction<System>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isLoading = false
      return state
    },
    patchSystemSuccess: (state, action: PayloadAction<RecordData>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isLoading = false
      return state
    },
    patchUrlDetailStatusSuccess: (state, action: PayloadAction<UrlDetailStatusRequest>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isLoading = false
      return state
    },
    getMailDeliverySuccess: (state, action: PayloadAction<MailDelivery>) => {
      state.mail_delivery = action.payload
      state.isLoading = false
      return state
    },
    patchMailDeliverySuccess: (state, action: PayloadAction<MailDelivery>) => {
      state.mail_delivery = action.payload
      state.isLoading = false
      return state
    },
    getMenuDisplaySuccess: (state, action: PayloadAction<MenuDisplay>) => {
      state.menu_display = action.payload.visible
      state.isLoading = false
      return state
    },
    patchMenuDisplaySuccess: (state, action: PayloadAction<MenuDisplay>) => {
      state.menu_display = action.payload.visible
      state.isLoading = false
      return state
    },
    getCustomAlertListSuccess: (state, action: PayloadAction<getCustomAlertListResponse[]>) => {
      state.custom_alert_list = action.payload
      return state
    },
    getAdministratorListSuccess: (state, action: PayloadAction<getAdministratorListResponse>) => {
      state.administrator_list = action.payload.admin_list
      return state
    },
    getCompanyServiceSuccess: (state, action: PayloadAction<CompanyService>) => {
      state.company_service = action.payload
      return state
    },
    getSlackChannelListSuccess: (state, action: PayloadAction<SlackChannelList[]>) => {
      state.slack_channel_list = action.payload
      return state
    },
    getIpRestrictionListSuccess: (state, action: PayloadAction<IpRestrictionList[]>) => {
      state.ip_restriction_list = action.payload
      state.isLoading = false
      return state
    },
    getOfficeIpListSuccess: (state, action: PayloadAction<OfficeIpList[]>) => {
      state.office_ip_list = action.payload
      state.isLoading = false
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
  },
})

export const {
  requestStart,
  getSystemSuccess,
  patchSystemSuccess,
  getMailDeliverySuccess,
  patchMailDeliverySuccess,
  patchUrlDetailStatusSuccess,
  getMenuDisplaySuccess,
  patchMenuDisplaySuccess,
  getCustomAlertListSuccess,
  getAdministratorListSuccess,
  getCompanyServiceSuccess,
  getSlackChannelListSuccess,
  getIpRestrictionListSuccess,
  getOfficeIpListSuccess,
  requestFailure,
} = systemSlice.actions

export const systemReducer = systemSlice.reducer

export const fetchSystem = (
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getSystem()
    .then((result) => {
      dispatch(getSystemSuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateSystem = (
  params: RecordData,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchSystem(params)
    .then((result) => {
      dispatch(patchSystemSuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateUrlStatus = (
  params: UrlDetailStatusRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchUrlDetailStatus(params)
    .then(() => {
      dispatch(patchUrlDetailStatusSuccess(params))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchMailDelivery = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getMailDelivery()
    .then((result) => {
      dispatch(getMailDeliverySuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateMailDelivery = (
  params: MailDelivery,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchMailDelivery(params)
    .then((result) => {
      dispatch(patchMailDeliverySuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchMenuDisplay = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getMenuDisplay()
    .then((result) => {
      dispatch(getMenuDisplaySuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateMenuDisplay = (
  params: MenuDisplay,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchMenuDisplay(params)
    .then((result) => {
      dispatch(patchMenuDisplaySuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchCustomAlertList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getCustomAlertList()
    .then((result) => {
      dispatch(getCustomAlertListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const createCustomAlert = (
  params: postCustomAlertRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  postCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateCustomAlert = (
  params: patchCustomAlertRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  patchCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateCustomAlertStatus = (
  params: patchCustomAlertForStatusRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  patchCustomAlertStatus(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const delCustomAlert = (
  params: deleteCustomAlertRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchAdministratorList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getAdministratorList()
    .then((result) => {
      dispatch(getAdministratorListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const createAdministrator = (
  params: postAdministratorRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  postAdministrator(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateAdministrator = (
  params: patchAdministratorRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  patchAdministrator(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const delAdministrator = (
  params: deleteAdministratorRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteAdministrator(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchCompanyService = (
): AppThunk => async (dispatch) => {
  getCompanyService()
    .then((result) => {
      dispatch(getCompanyServiceSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchMfaQrCode = (
  callback: (result: {url: string, google_secret: string}) => void,
): AppThunk => async (dispatch) => {
  getMfaQrCode()
    .then((result) => {
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateMfaAuth = (
  params: patchMfaAuthRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  patchMfaAuth(params)
    .then(() => {
      dispatch(updateCommonMfaAuth())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const delMfaAuth = (
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteMfaAuth()
    .then(() => {
      dispatch(deleteCommonMfaAuth())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchSlackChannelList = (
): AppThunk => async (dispatch) => {
  getSlackChannelList()
    .then((result) => {
      dispatch(getSlackChannelListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchAllowAccess = (
  failedCallback: () => void,
): AppThunk => async (dispatch) => {
  getAllowAccess()
    .then()
    .catch(() => {
      dispatch(requestFailure())
      failedCallback()
    })
}

export const fetchIpRestrictionList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getIpRestrictionList()
    .then((result) => {
      dispatch(getIpRestrictionListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateIpRestrictionList = (
  params: patchIpRestrictionListRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  patchIpRestrictionList(params)
    .then(() => {
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
      errorCallback()
    })
}

export const fetchOfficeIpList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getOfficeIpList()
    .then((result) => {
      dispatch(getOfficeIpListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateOfficeIpList = (
  params: patchOfficeIpListRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  patchOfficeIpList(params)
    .then(() => {
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
      errorCallback()
    })
}
